/**
 * ModuleFacetBar — 카탈로그 패싯 필터 바 (queue #640 · docs/biz-specs/common-showcase.md
 * §UAT-2026-07-07-R640 §R640-1/§R640-5/§R640-6)
 *
 * searchbox 직후·트리 직전에 삽입되는 5 패싯(카테고리/상태/라이브데모/opt-in/biz 채택)
 * 드롭다운 + 활성 필터 칩 + 필터 초기화 + 결과 카운트. 필터 상태의 단일 진실은 URL
 * (`useSearchParams`) — 이 컴포넌트는 로컬 state 를 두지 않고 `searchParams`/`setSearchParams`
 * 를 그대로 읽고 쓴다(부모 `CommonShowcasePage` 가 훅을 소유). predicate/derive 계산은
 * `data/facet-predicates.ts` 공유 헬퍼로 위임(페이지 selector 와 동일 로직 재사용).
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ModuleNode } from '../types/showcase';
import { distinctArtifacts } from '../data/facet-predicates';

interface Props {
  nodes: ModuleNode[];
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParams, navigateOpts?: { replace?: boolean }) => void;
  visibleCount: number;
  totalCount: number;
}

// queue #677 §UAT-2026-07-13-R677 §R677-4②: 'watch' 패싯 추가(구독 모듈만) — §R640 계보.
const FACET_KEYS = ['category', 'status', 'demo', 'optin', 'biz', 'watch'] as const;
type FacetKey = (typeof FACET_KEYS)[number];

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-1)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
};

const selectStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-xs)',
  fontFamily: 'var(--font-family-sans)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-primary)',
};

export function ModuleFacetBar({ nodes, searchParams, setSearchParams, visibleCount, totalCount }: Props) {
  const { t } = useTranslation();

  const categoryOptions = useMemo(() => distinctArtifacts(nodes), [nodes]);

  const category = searchParams.get('category') ?? '';
  const status = searchParams.get('status') ?? '';
  const demo = searchParams.get('demo') ?? '';
  const optin = searchParams.get('optin') ?? '';
  const biz = searchParams.get('biz') ?? '';
  const watch = searchParams.get('watch') ?? '';

  // §R640-3: 빈 값 정리(파라미터 삭제) + replace(히스토리 스팸 방지).
  function setParam(key: FacetKey, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  }

  function removeParam(key: FacetKey) {
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    setSearchParams(next, { replace: true });
  }

  function resetAll() {
    const next = new URLSearchParams(searchParams);
    FACET_KEYS.forEach((k) => next.delete(k));
    next.delete('q');
    setSearchParams(next, { replace: true });
  }

  // 활성 칩(값이 인식되는 파라미터만 — 오타 딥링크는 chip 미생성·predicate 도 무시).
  const chips: { key: FacetKey; facetLabel: string; valueLabel: string }[] = [];
  if (category) chips.push({ key: 'category', facetLabel: t('showcase.filter.category.label'), valueLabel: category });
  if (status === 'active' || status === 'superseded') {
    chips.push({ key: 'status', facetLabel: t('showcase.filter.status.label'), valueLabel: t(`showcase.filter.status.${status}`) });
  }
  if (demo === 'true' || demo === 'false') {
    chips.push({
      key: 'demo',
      facetLabel: t('showcase.filter.demo.label'),
      valueLabel: t(demo === 'true' ? 'showcase.filter.demo.yes' : 'showcase.filter.demo.no'),
    });
  }
  if (optin === 'true' || optin === 'false' || optin === 'na') {
    const optinKey = optin === 'true' ? 'optin' : optin === 'false' ? 'always' : 'na';
    chips.push({ key: 'optin', facetLabel: t('showcase.filter.optin.label'), valueLabel: t(`showcase.filter.optin.${optinKey}`) });
  }
  if (biz === 'true' || biz === 'false') {
    chips.push({
      key: 'biz',
      facetLabel: t('showcase.filter.biz.label'),
      valueLabel: t(biz === 'true' ? 'showcase.filter.biz.yes' : 'showcase.filter.biz.no'),
    });
  }
  // queue #677 §R677-4②: watch=1 은 값이 아니라 boolean 토글이라 칩의 facetLabel/valueLabel
  // 을 동일 문자열로 채운다(다른 select 패싯과 달리 "값"이 없음).
  if (watch === '1') {
    chips.push({ key: 'watch', facetLabel: t('showcase.filter.watch'), valueLabel: t('showcase.filter.demo.yes') });
  }

  const hasActiveFilters = chips.length > 0;

  return (
    <div
      role="search"
      aria-label={t('showcase.filter.barAria')}
      style={{
        padding: 'var(--spacing-3)',
        borderBottom: '1px solid var(--color-surface-border)',
        background: 'var(--color-surface-background)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
        <label style={labelStyle}>
          <span>{t('showcase.filter.category.label')}</span>
          <select style={selectStyle} value={category} onChange={(e) => setParam('category', e.target.value)}>
            <option value="">{t('showcase.filter.category.all')}</option>
            {categoryOptions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </label>

        <label style={labelStyle}>
          <span>{t('showcase.filter.status.label')}</span>
          <select style={selectStyle} value={status} onChange={(e) => setParam('status', e.target.value)}>
            <option value="">{t('showcase.filter.status.all')}</option>
            <option value="active">{t('showcase.filter.status.active')}</option>
            <option value="superseded">{t('showcase.filter.status.superseded')}</option>
          </select>
        </label>

        <label style={labelStyle}>
          <span>{t('showcase.filter.demo.label')}</span>
          <select style={selectStyle} value={demo} onChange={(e) => setParam('demo', e.target.value)}>
            <option value="">{t('showcase.filter.demo.all')}</option>
            <option value="true">{t('showcase.filter.demo.yes')}</option>
            <option value="false">{t('showcase.filter.demo.no')}</option>
          </select>
        </label>

        <label style={labelStyle}>
          <span>{t('showcase.filter.optin.label')}</span>
          <select style={selectStyle} value={optin} onChange={(e) => setParam('optin', e.target.value)}>
            <option value="">{t('showcase.filter.optin.all')}</option>
            <option value="true">{t('showcase.filter.optin.optin')}</option>
            <option value="false">{t('showcase.filter.optin.always')}</option>
            <option value="na">{t('showcase.filter.optin.na')}</option>
          </select>
        </label>

        <label style={labelStyle}>
          <span>{t('showcase.filter.biz.label')}</span>
          <select style={selectStyle} value={biz} onChange={(e) => setParam('biz', e.target.value)}>
            <option value="">{t('showcase.filter.biz.all')}</option>
            <option value="true">{t('showcase.filter.biz.yes')}</option>
            <option value="false">{t('showcase.filter.biz.no')}</option>
          </select>
        </label>

        {/* queue #677 §R677-4②: '구독 모듈만' 토글 — select 가 아닌 checkbox(boolean 단일 값) */}
        <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 'var(--spacing-2)' }}>
          <input
            type="checkbox"
            checked={watch === '1'}
            onChange={(e) => setParam('watch', e.target.checked ? '1' : '')}
          />
          <span>{t('showcase.filter.watch')}</span>
        </label>
      </div>

      {hasActiveFilters && (
        <div
          role="group"
          aria-label={t('showcase.filter.activeAria')}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-1)', marginTop: 'var(--spacing-2)' }}
        >
          {chips.map((chip) => (
            <span
              key={chip.key}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-1)',
                fontSize: 'var(--font-size-xs)',
                background: 'var(--color-surface-muted)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-full)',
                padding: '2px var(--spacing-2)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {chip.facetLabel}: {chip.valueLabel}
              <button
                type="button"
                aria-label={t('showcase.filter.removeChip', { facet: chip.facetLabel })}
                onClick={() => removeParam(chip.key)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  color: 'var(--color-text-muted)',
                  fontSize: 'var(--font-size-xs)',
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 'var(--spacing-3)',
          gap: 'var(--spacing-2)',
        }}
      >
        <button
          type="button"
          onClick={resetAll}
          disabled={!hasActiveFilters}
          style={{
            background: 'none',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-sm)',
            padding: '2px var(--spacing-2)',
            fontSize: 'var(--font-size-xs)',
            color: hasActiveFilters ? 'var(--color-brand-primary)' : 'var(--color-text-muted)',
            cursor: hasActiveFilters ? 'pointer' : 'not-allowed',
            fontFamily: 'var(--font-family-sans)',
          }}
        >
          {t('showcase.filter.reset')}
        </button>
        <span aria-live="polite" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          {t('showcase.filter.count', { visible: visibleCount, total: totalCount })}
        </span>
      </div>
    </div>
  );
}
